/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  SUPPORTED_CURRENCIES,
  SupportedCurrencies,
} from '@airasia-rn/ui/config/currencies';
import {
  SUPPORTED_LANGUAGES,
  SupportedLocales,
} from '@airasia-rn/ui/localization';
import { CurrencyItem } from '@airasia-rn/ui/widgets';
import type { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import { CurrencyInfo, LayoutData, RawLayoutData } from './types';

const isSupportedLocale = (locale: string): locale is SupportedLocales =>
  locale in SUPPORTED_LANGUAGES;

const isSupportedCurrency = (
  currency: string,
): currency is SupportedCurrencies => currency in SUPPORTED_CURRENCIES;

export const mapSupportedLanguages = (
  supportedLanguages?: Record<string, string>,
): MemoizedListViewProps[] | undefined => {
  if (!supportedLanguages) return undefined;

  return Object.entries(supportedLanguages)
    .filter(([locale]) => isSupportedLocale(locale))
    .map(([locale, label]) => ({
      id: locale,
      primaryText: `${locale} - ${label}`,
      model: { id: locale },
      title: '',
    }));
};

export const mapSupportedCurrencies = (
  supportedCurrencies?: Record<string, CurrencyInfo>,
): CurrencyItem[] | undefined => {
  if (!supportedCurrencies) return undefined;

  return Object.entries(supportedCurrencies)
    .filter(([currency]) => isSupportedCurrency(currency))
    .map(([currency, info]) => ({
      id: currency,
      primaryText: `${currency} - ${info.longform}`,
    }));
};

export const mapLayoutData = (data: RawLayoutData): LayoutData => ({
  header: data.header,
  footer: data.footer,
  localeList: mapSupportedLanguages(data.supported_languages),
  currencyList: mapSupportedCurrencies(data.supported_currencies),
  cookieDisclaimer: data.cookieDisclaimer,
  loginToolTip: data.loginToolTip,
});
